import { Card, CardContent, Button, Grid, Typography } from '@material-ui/core'
import React, { useState, useEffect } from 'react'
import {
  EmissionsForJourneysFragment
  , useGetEmissionsForLocationsQuery
} from './Sustainability.generated-types'
import { Accordion, AccordionDetails, AccordionSummary, Chip, ChipProps } from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { BlackBusIcon } from '../../../../Components/Icons/Icons'
import { EmissionMessage } from './EmissionMessage'
import { EmissionTable } from './EmissionTable'
import './style.less'
import { JOURNEYS_SERVICE } from '../../../../Constants'

interface SustainabilityProps {
  proposalUUID: string
  eventUUID: string
}

const LocationChip = ({
  location,
  size = 'small',
  ...rest
}: { location: string, size?: 'small' | 'medium' } & Omit<
ChipProps,
'size'
>): JSX.Element => (
  <Chip
    size={size}
    icon={<BlackBusIcon titleAccess='Nearest station' />}
    label={location}
    {...rest}
  />
)

const roundToTwo = (num): number => {
  return Math.round(num * 100) / 100
}

export const Sustainability: React.FC<SustainabilityProps> = ({ proposalUUID, eventUUID }) => {
  const [{ data }] = useGetEmissionsForLocationsQuery({
    variables: { input: { locationIds: [proposalUUID] } },
  })

  const [emissions, setEmissions] = useState<EmissionsForJourneysFragment[]>([])
  const [emissionsByJourneyType, setEmissionsByJourneyType] = useState([])
  const [totalEmission, setTotalEmission] = useState(0)
  const [totalCalculatedEmissionPerPerson, setTotalCalculatedEmissionPerPerson] = useState(0)

  const sustainabilityToolUrl = `${JOURNEYS_SERVICE}/${eventUUID}/journeys`

  useEffect(() => {
    if (data?.getEmissionsForLocations?.length) {
      const emissions: EmissionsForJourneysFragment[] = data.getEmissionsForLocations[0].emissionsForJourneys
      setEmissions(emissions)
      const emissionReducing = []

      emissions.reduce((res, value) => {
        if (!res[value.journey.journeyType]) {
          res[value.journey.journeyType] = {
            journeyType: value.journey.journeyType,
            totalCalculatedEmissions: value.calculatedEmissions,
            totalNumberOfPeople: value.journey.numberOfPeople,
            calculatedEmissionPerPerson: value.calculatedEmissions / value.journey.numberOfPeople,
          }
          emissionReducing.push(res[value.journey.journeyType])
        } else {
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          res[value.journey.journeyType].totalNumberOfPeople += value.journey.numberOfPeople
          // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
          res[value.journey.journeyType].totalCalculatedEmissions += value.calculatedEmissions
          res[value.journey.journeyType].calculatedEmissionPerPerson = res[value.journey.journeyType].totalCalculatedEmissions / res[value.journey.journeyType].totalNumberOfPeople
        }
        return res
      }, {})

      setTotalEmission(emissionReducing.reduce((n: number, { totalCalculatedEmissions }: { totalCalculatedEmissions: number }) => n + totalCalculatedEmissions, 0))
      setTotalCalculatedEmissionPerPerson(emissionReducing.reduce((n: number, { calculatedEmissionPerPerson }: { calculatedEmissionPerPerson: number }) => n + calculatedEmissionPerPerson, 0))
      setEmissionsByJourneyType(emissionReducing)
    }
  }, [data])

  if (!emissions.length) {
    return (
      <Card className='journeysEmptyMessage'>
        <CardContent>
          <Typography className='message' variant="body1">
          Add travel details to see your projected emissions
          </Typography>
          <Button
            className="link"
            size="small"
            target="_blank"
            href={sustainabilityToolUrl}
          >
          add travel information
          </Button>
        </CardContent>
      </Card>
    )
  }

  return (
    <Grid container spacing={6}>
      <Grid item xs={12}>
        <Accordion style={{ backgroundColor: '#fafafa', boxShadow: 'none' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel2a-content"
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 500, alignItems: 'center' }}>
              <LocationChip
                size='medium'
                sx={{ fontFamily: 'Montserrat', backgroundColor: '#fafafa', fontSize: 16 }}
                location={'Travel'}
              />
              <span>{roundToTwo(totalEmission)}kg CO2e</span>
            </div>
          </AccordionSummary>
          <AccordionDetails style={{ paddingLeft: 40 }}>
            <EmissionTable
              emissionsByJourneyType={emissionsByJourneyType}
              totalCalculatedEmissionPerPerson={totalCalculatedEmissionPerPerson} />
            <EmissionMessage
              emissions={emissions} />
          </AccordionDetails>

        </Accordion>
        <Accordion style={{ backgroundColor: '#fafafa', boxShadow: 'none' }} disabled>
          <AccordionSummary>
            <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontWeight: 600 }}>
              <span>
                Total
              </span>
              <span>{roundToTwo(totalEmission)}kg CO2e</span>
            </div>
          </AccordionSummary>
        </Accordion>
      </Grid>
    </Grid>
  )
}
